import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function NewsletterBand() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (honeypot || !email.trim()) return;

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          name: "",
          honeypot,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setStatus("success");
      setEmail("");
    } catch (err: any) {
      console.error("Newsletter subscription failed:", err);
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");

      setTimeout(() => {
        setStatus("idle");
        setErrorMsg("");
      }, 4000);
    }
  };

  return (
    <section className="relative overflow-hidden bg-primary">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-black blur-3xl" />

        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 20px,
              rgba(0,0,0,0.04) 20px,
              rgba(0,0,0,0.04) 21px
            )`,
          }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <AnimatePresence mode="wait">
          {status === "success" ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-4"
            >
              <div className="w-16 h-16 rounded-full bg-black/20 flex items-center justify-center">
                <CheckCircle2 size={36} className="text-white" />
              </div>

              <h3 className="text-2xl font-display font-bold text-black">
                You're subscribed!
              </h3>

              <p className="text-black/70 font-medium">
                Welcome aboard. We'll keep you updated with the latest from
                Eliada Sports Agency.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <p className="text-black/60 font-semibold uppercase tracking-widest text-sm mb-3">
                Newsletter
              </p>

              <h2 className="text-3xl md:text-5xl font-display font-bold text-black mb-4 leading-tight">
                STAY IN THE LOOP
              </h2>

              <p className="text-black/70 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                Get the latest athlete signings, agency news, and exclusive
                updates delivered straight to your inbox.
              </p>

              <input
                type="text"
                name="website_url"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="absolute -left-[9999px] w-0 h-0 overflow-hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  disabled={status === "loading"}
                  className="flex-1 px-5 py-4 rounded-lg bg-black/10 border border-black/20 text-black placeholder:text-black/50 focus:outline-none focus:border-black/40 focus:bg-black/15 text-sm font-medium transition-all disabled:opacity-60"
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="flex items-center justify-center gap-2 bg-black text-white font-display font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-lg hover:bg-black/80 active:scale-95 transition-all disabled:opacity-60 whitespace-nowrap"
                >
                  <Send size={15} />
                  {status === "loading" ? "Subscribing…" : "Subscribe"}
                </button>
              </form>

              {status === "error" && (
                <motion.p
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 text-sm text-black/70 font-medium"
                >
                  {errorMsg}
                </motion.p>
              )}

              <p className="mt-5 text-xs text-black/50">
                No spam, ever. Unsubscribe at any time.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
